import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  HbrAccordion,
  HbrBadge,
  HbrIcon,
  HbrInput,
  HbrOption,
  HbrSelect,
  HbrSpinner,
  HbrTextarea,
} from '@harbor/elements-react-standalone';
import StbButton from '../../../security-toolbox-ui/StbButton';
import { useModal } from '../../../security-toolbox-ui/StbModalContext';
import DesignPhaseUseCase from './DesignPhaseUseCase';
import { Product } from '../../../../types/product';
import {
  useAddPovUseCaseMutation,
  useDeletePovOutcomeMutation,
  useUpdatePovUseCaseMutation,
  useFetchOutcomeUseCases
} from '../../../../hooks/opportunity-pov-hooks';
import { useFetchOpportunity, useFetchOpportunityProducts } from '../../../../hooks/opportunity-hooks';
import { CacheKey } from '../../../../types/cache-keys';
import { useFetchPov } from '../../opportunityDetailHelpers';
import { Opportunity } from '../../../../types/opportunity';
import { PoVOutcome, UseCase } from '../../../../types/opportunity-pov-items';
import StbModal from '../../../security-toolbox-ui/StbModal';
import { statusWarning } from '@harbor/elements/icons';
import StbApiModal from '../../../security-toolbox-ui/StbApiModal';
import { asSingleValue, unicodeToPlain } from '../../../../support/utils';

interface Props {
  outcome: PoVOutcome;
  opportunityId: Opportunity['id'];
  editOC: () => void;
  updated: () => void;
}

const styles = {
  grid: 'grid grid-cols-5 gap-8 w-full',
};

const OTHER_OPTION_VALUE = '__other__';

type ProductFeatureSuccessCriteria = {
  id: number;
  title?: string;
  name?: string;
  description?: string;
};

type ProductUseCaseOption = {
  id: number;
  title?: string;
  name?: string;
  description?: string;
  product_feature_success_criteria?: ProductFeatureSuccessCriteria[];
};

type ProductWithCatalog = Product & {
  product_use_case?: ProductUseCaseOption[];
  product_use_cases?: ProductUseCaseOption[];
  use_case?: ProductUseCaseOption[];
};

type UseCaseWithLineage = UseCase & {
  product_use_case?: number | null;
  is_custom?: boolean;
};

type EditingUseCase = {
  id?: number;
  title: string;
  description: string;
  pov_outcome: number;
  product: number | null;
  product_use_case: number | null;
  is_custom: boolean;
};

const DesignPhaseOutcome = ({
                              outcome,
                              opportunityId,
                              editOC,
                              updated,
                            }: Props) => {
  const queryClient = useQueryClient();

  const { data: opportunity } = useFetchOpportunity(opportunityId, { enabled: !!opportunityId });
  const [currentlyEditing, setCurrentlyEditing] = useState<EditingUseCase | null>(null);
  const [titleTouched, setTitleTouched] = useState(false);

  const { mutateAsync: deleteOutcome } = useDeletePovOutcomeMutation();
  const { data: useCases, isLoading: useCasesLoading } = useFetchOutcomeUseCases(opportunityId, outcome.id);
  const { data: products, isLoading: isLoadingProducts } = useFetchOpportunityProducts(opportunityId);
  const { data: pov } = useFetchPov(opportunity);
  const { mutateAsync: createUseCase } = useAddPovUseCaseMutation(String(pov?.id));
  const { mutateAsync: updateUseCase } = useUpdatePovUseCaseMutation(String(pov?.id));

  const { openModal, closeModal } = useModal();

  const ciscoProducts = useMemo(
    () => (products ?? []).filter((product: Product) => product.vendor?.is_cisco),
    [products]
  );

  const selectedProduct = useMemo(
    () =>
      ciscoProducts.find((p: Product) => p.id === currentlyEditing?.product) as
        | ProductWithCatalog
        | undefined,
    [ciscoProducts, currentlyEditing?.product]
  );

  const selectedProductUseCases: ProductUseCaseOption[] = useMemo(() => {
    return (
      selectedProduct?.use_case ||
      selectedProduct?.product_use_case ||
      selectedProduct?.product_use_cases ||
      []
    );
  }, [selectedProduct]);

  const selectedUseCase = useMemo(() => {
    if (!currentlyEditing?.product_use_case) return undefined;
    return selectedProductUseCases.find((uc) => uc.id === currentlyEditing.product_use_case);
  }, [currentlyEditing?.product_use_case, selectedProductUseCases]);

  const selectedUseCaseValue = useMemo(() => {
    if (currentlyEditing?.is_custom) return OTHER_OPTION_VALUE;
    if (currentlyEditing?.product_use_case) return String(currentlyEditing.product_use_case);
    return '';
  }, [currentlyEditing?.is_custom, currentlyEditing?.product_use_case]);

  const selectedProductValue = useMemo(() => {
    return currentlyEditing?.product ? String(currentlyEditing.product) : '';
  }, [currentlyEditing?.product]);

  const handleOpenDeleteConfirmModal = () => {
    openModal({
      content: (
        <StbModal
          open={true}
          role="delete"
          onSubmit={handleDeleteOutcome}
          onClose={async () => closeModal()}
        >
          <div>Are you sure you want to delete this outcome?</div>
        </StbModal>
      )
    });
  };

  const handleDeleteOutcome = async () => {
    await deleteOutcome(outcome as any);
    updated();
    queryClient.invalidateQueries({ queryKey: [CacheKey.PovOutcomes, outcome.id] });
  };

  const handleProductChange = (productIdValue: string) => {
    const productId = Number(productIdValue);

    setCurrentlyEditing((prev) => ({
      id: prev?.id,
      pov_outcome: outcome.id,
      product: productId || null,
      product_use_case: null,
      is_custom: false,
      title: '',
      description: '',
    }));

    setTitleTouched(false);
  };

  const handleUseCaseSelection = (value: string) => {
    if (!currentlyEditing) return;

    if (value === OTHER_OPTION_VALUE) {
      setCurrentlyEditing({
        ...currentlyEditing,
        product_use_case: null,
        is_custom: true,
        title: currentlyEditing.title || '',
        description: currentlyEditing.description || '',
      });
      return;
    }

    const selectedId = Number(value);
    const catalogUseCase = selectedProductUseCases.find((uc) => uc.id === selectedId);
    if (!catalogUseCase) return;

    setCurrentlyEditing({
      ...currentlyEditing,
      product_use_case: catalogUseCase.id,
      is_custom: false,
      title: (catalogUseCase.title || catalogUseCase.name || '').trim(),
      description: (catalogUseCase.description || '').trim(),
    });
    setTitleTouched(false);
  };

  const handleSave = async () => {
    if (!currentlyEditing?.product) return;

    const trimmedTitle = currentlyEditing.title.trim();
    const trimmedDescription = currentlyEditing.description.trim();

    if (currentlyEditing.is_custom && !trimmedTitle) {
      setTitleTouched(true);
      return;
    }

    if (!currentlyEditing.is_custom && !currentlyEditing.product_use_case) {
      return;
    }

    if (currentlyEditing.id) {
      const payload: UseCaseWithLineage = {
        id: currentlyEditing.id,
        title: trimmedTitle,
        description: trimmedDescription,
        pov_outcome: currentlyEditing.pov_outcome,
        product: currentlyEditing.product,
        product_use_case: currentlyEditing.product_use_case,
        is_custom: currentlyEditing.is_custom,
        type: 'Use Case',
      };
      await updateUseCase(payload as any);
    } else {
      const payload: Omit<UseCaseWithLineage, 'id'> = {
        title: trimmedTitle,
        description: trimmedDescription,
        pov_outcome: currentlyEditing.pov_outcome,
        product: currentlyEditing.product,
        product_use_case: currentlyEditing.product_use_case,
        is_custom: currentlyEditing.is_custom,
        type: 'Use Case',
      };
      await createUseCase(payload as any);
    }

    updated();
    setTitleTouched(false);
    setCurrentlyEditing(null);
  };

  return (
    <HbrAccordion variant="stacked" className="mb-2" open={true}>
      <header slot="label" className={styles.grid}>
        <div className="col-span-2 flex items-center">
          <h3 className="flex-grow">{outcome.title}</h3>

          <div className="flex-grow-0 min-w-fit">
            <StbButton
              iconOnly={true}
              role="update"
              size="small"
              variant="text"
              className="mr-2"
              onClick={(e) => {
                e.stopPropagation();
                editOC();
              }}
            />
            <StbButton
              iconOnly={true}
              role="delete"
              size="small"
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteConfirmModal();
              }}
            />
          </div>
        </div>

        <div className="flex items-center">
          {!!useCases?.length && (
            <>
              <span className="pl-4 col-span-3 leading-7 mr-2">Use&nbsp;Cases</span>
              <HbrBadge slot="suffix" pill size="small">
                {useCases?.length}
              </HbrBadge>
            </>
          )}
        </div>
      </header>

      <div className={styles.grid}>
        <div className="col-span-2 text-sm">{outcome.description}</div>

        <div className="col-span-3">
          {useCasesLoading && <HbrSpinner size="small" />}

          {useCases?.map((useCase) => {
            const useCaseWithLineage = useCase as UseCaseWithLineage;
            return (
              <DesignPhaseUseCase
                useCase={useCaseWithLineage}
                opportunityId={opportunityId}
                key={useCase.id}
                editUC={() =>
                  setCurrentlyEditing({
                    id: useCase.id,
                    title: useCase.title || '',
                    description: useCase.description || '',
                    pov_outcome: outcome.id,
                    product: useCase.product ?? null,
                    product_use_case: useCaseWithLineage.product_use_case ?? null,
                    is_custom: useCaseWithLineage.is_custom ?? !useCaseWithLineage.product_use_case,
                  })
                }
                updated={updated}
              />
            );
          })}

          <StbButton
            role="add"
            size="medium"
            variant="text"
            onClick={() =>
              setCurrentlyEditing({
                title: '',
                description: '',
                pov_outcome: outcome.id,
                product: null,
                product_use_case: null,
                is_custom: false,
              })
            }
          >
            Add Use Case
          </StbButton>

          <StbApiModal
            open={!!currentlyEditing}
            onSubmit={handleSave}
            onClose={async () => {
              setTitleTouched(false);
              setCurrentlyEditing(null);
            }}
            submitLabel={`${currentlyEditing?.id ? 'Update' : 'Save'} Use Case`}
            submitDisabled={
              !currentlyEditing?.product ||
              (currentlyEditing?.is_custom
                ? !currentlyEditing?.title.trim()
                : !currentlyEditing?.product_use_case)
            }
            submitSuccess={`Use Case ${currentlyEditing?.id ? 'updated' : 'created'} successfully`}
            submitError={`Failed to ${currentlyEditing?.id ? 'update' : 'create'} Use Case`}
            title={`${currentlyEditing?.id ? 'Edit' : 'Add'} Use Case`}
            size="medium"
            role={currentlyEditing?.id ? 'update' : 'save'}
          >
            <div className="flex-col flex">
              {isLoadingProducts && <HbrSpinner size="small" />}

              {products?.length === 0 && (
                <div
                  className="p-3 rounded-md mb-3 flex items-start gap-3 text-sm"
                  style={{ border: '1px solid var(--hbr-color-warning-35)' }}
                >
                  <HbrIcon size="24px" asset={statusWarning} sentiment="warning" />
                  <div>Add products to <em>Opportunity Solutions</em></div>
                </div>
              )}

              {ciscoProducts?.length > 0 && (
                <HbrSelect
                  value={selectedProductValue}
                  placeholder="Select product"
                  className="mb-4"
                  onHbr-change={(e) => handleProductChange(asSingleValue(e.target.value))}
                >
                  <div slot="label">Product <em className="text-gray-500">(required)</em></div>
                  {ciscoProducts.map((product: Product) => (
                    <HbrOption key={product.id} value={String(product.id)}>
                      {product.name}
                    </HbrOption>
                  ))}
                </HbrSelect>
              )}

              {!!currentlyEditing?.product && (
                <HbrSelect
                  value={selectedUseCaseValue}
                  placeholder="Select use case"
                  className="mb-4"
                  onHbr-change={(e) => handleUseCaseSelection(asSingleValue(e.target.value))}
                >
                  <div slot="label">Use Case <em className="text-gray-500">(required)</em></div>
                  {selectedProductUseCases.map((uc) => (
                    <HbrOption key={uc.id} value={String(uc.id)}>
                      {uc.title || uc.name}
                    </HbrOption>
                  ))}
                  <HbrOption value={OTHER_OPTION_VALUE}>
                    {selectedProductUseCases.length ? 'Other' : 'Custom Use Case'}
                  </HbrOption>
                </HbrSelect>
              )}

              {currentlyEditing?.is_custom && (
                <>
                  <HbrInput
                    value={currentlyEditing?.title}
                    placeholder="Add a use case name"
                    className="grow required mt-2"
                    error={titleTouched && !currentlyEditing?.title.trim()}
                    onHbr-input={(e) => {
                      setCurrentlyEditing({
                        ...currentlyEditing,
                        title: unicodeToPlain(e.target.value),
                      });
                      setTitleTouched(true);
                    }}
                    required
                  >
                    <div slot="label">Name <em className="text-gray-500">(required)</em></div>
                    {titleTouched && !currentlyEditing?.title.trim() && (
                      <div slot="help-text" className="text-sm mt-1 text-danger">
                        Name is required
                      </div>
                    )}
                  </HbrInput>

                  <HbrTextarea
                    value={currentlyEditing?.description}
                    onHbr-input={(e) =>
                      setCurrentlyEditing({
                        ...currentlyEditing,
                        description: unicodeToPlain(e.target.value),
                      })
                    }
                    label="Description"
                  />
                </>
              )}

              {!currentlyEditing?.is_custom && selectedUseCase?.description && (
                <div className="mt-1 text-sm text-gray-600">
                  {selectedUseCase.description}
                </div>
              )}
            </div>
          </StbApiModal>
        </div>
      </div>
    </HbrAccordion>
  );
};

export default DesignPhaseOutcome;